import { MatchEvent } from 'shared/types/match-event';

/**
 * Отбирает из журнала матча события, которых оверлей ещё не видел.
 *
 * Журнал приходит целиком (`matchChronology.events` — весь массив на каждое обновление), поэтому
 * «новое» — это событие, не встречавшееся раньше. Первый вызов задаёт базовую линию: всё, что было
 * в журнале на момент подключения к программе, меткой по получению не помечается — момент
 * получения для них уже прошёл.
 *
 * Событие узнаётся по id **или** по содержимому — тип, команда, тайм, секунда и игроки. Приложение
 * статиста при повторном открытии матча раздаёт старым событиям новые id (прогон IMGM-1484, матч
 * 63759: три старых гола стали 1791441887620–622), и по одному id все они выглядели бы новыми. Цена
 * — два настоящих события одного типа у одной команды с теми же игроками в одну секунду сольются
 * в одно; на практике так не бывает.
 *
 * Однажды увиденное событие новым больше не станет, даже если пропало из журнала и вернулось: это
 * то же событие, и метка на него уже есть (или не нужна).
 */
export class NewEventsTracker {
  private seenIds: Set<number> | null = null;
  private seenContent = new Set<string>();

  /** Новые события этого снимка журнала; на первом снимке — пусто. */
  take(events: readonly MatchEvent[]): MatchEvent[] {
    if (this.seenIds === null) {
      this.seenIds = new Set();
      events.forEach((event) => this.remember(event));
      return [];
    }

    const fresh: MatchEvent[] = [];
    for (const event of events) {
      const isSeen = this.seenIds.has(event.id) || this.seenContent.has(contentKey(event));
      this.remember(event);
      if (!isSeen) fresh.push(event);
    }

    return fresh;
  }

  /** Забыть базовую линию — при смене матча или переподключении к программе. */
  reset() {
    this.seenIds = null;
    this.seenContent = new Set();
  }

  private remember(event: MatchEvent) {
    this.seenIds!.add(event.id);
    this.seenContent.add(contentKey(event));
  }
}

function contentKey(event: MatchEvent): string {
  const playerIds = event.players.map((player) => player.id).join(',');
  return [event.type, event.teamId, event.period, event.seconds, playerIds].join('|');
}
